// Phase 56.5: ErrorBoundary fallback updated to render the branded
// ServerError page. Behavior + lib dependency preserved (uses
// react-error-boundary as before); only the fallback UI changed
// from inline JSX to the centralized ServerError component so 500
// errors look identical to the wildcard 404 page family.
//
// HANDOFF rule compliance: ServerError.tsx strips dashes from the
// error message before rendering; this file passes the raw message
// through and lets ServerError handle sanitization.

import type { ReactNode } from 'react'
import { ErrorBoundary as ReactErrorBoundary, type FallbackProps } from 'react-error-boundary'
import { captureException } from '../lib/report'
import { ServerError } from '../pages/ServerError'
import { isDynamicImportError, reloadOnceForStaleChunk } from '../lib/staleChunk'
import { BUILD_ID } from '../lib/loadFailure'

// Plain transitional screen shown for the instant before the reload navigates
// away. No em dashes or hyphens in copy.
function StaleChunkReloading() {
  return (
    <main className="flex min-h-screen items-center justify-center bg-muted/50 px-4">
      <p className="text-sm text-muted-foreground">Loading the latest version</p>
    </main>
  )
}

/** Never throws: a reporting failure must not become a second error on a path
 *  that is already handling one. */
function report(error: unknown, kind: 'stale_chunk' | 'render') {
  try {
    captureException(
      error instanceof Error ? error : new Error(String(error ?? 'unknown render error')),
      {
        tags: { boundary: 'app', boundary_kind: kind, build_id: BUILD_ID },
        extra: { build_id: BUILD_ID, path: window.location.pathname },
      },
    )
  } catch {
    // Sentry unavailable. The fallback UI below still renders.
  }
}

function FallbackUI({ error, resetErrorBoundary }: FallbackProps) {
  // A failed dynamic import is almost always a stale route chunk after a deploy.
  // Reload once (guarded) so the user silently lands on the route they were
  // navigating to. If the guard says we already reloaded (a real failure / truly
  // missing asset), fall through to the normal error screen. Any non chunk error
  // is untouched and still shows "We are working on it".
  if (isDynamicImportError(error)) {
    // RELIABILITY-1: REPORT BEFORE RELOADING.
    //
    // This branch self-heals, which is exactly why it was invisible: the tab
    // reloads and nobody ever hears about it. Reporting first is the only way
    // to see how often a deploy is stranding open tabs, and reloadOnce...()
    // navigates away immediately, so anything after it never runs.
    report(error, 'stale_chunk')
    if (reloadOnceForStaleChunk()) return <StaleChunkReloading />
    // The guard refused (already reloaded once recently): a genuinely broken
    // deploy or a truly missing asset. Fall through to the error screen.
  }

  // react-error-boundary types `error` as `{}` (unknown) since
  // anything can be thrown. Narrow to Error before reading
  // message; defensive against thrown strings / objects.
  const message =
    error instanceof Error ? error.message : typeof error === 'string' ? error : undefined
  return <ServerError errorMessage={message} onRetry={resetErrorBoundary} />
}

interface Props {
  children: ReactNode
}

export function ErrorBoundary({ children }: Props) {
  // RELIABILITY-1: react-error-boundary swallows the error by design, and
  // Sentry's global window.onerror never sees a React error that a boundary
  // caught. Without this, every crash in the app was invisible in production.
  // The stale-chunk branch reports inside FallbackUI instead, because it must
  // report before it navigates away; onError would fire either way, so this
  // reports only what the fallback did NOT already handle.
  return (
    <ReactErrorBoundary
      FallbackComponent={FallbackUI}
      onError={(error) => {
        if (isDynamicImportError(error)) return
        report(error, 'render')
      }}
    >
      {children}
    </ReactErrorBoundary>
  )
}
